import React from 'react';
import { Field } from 'redux-form';
import classNames from 'clsx';

import { UtilService } from 'app/services/util.service';
import getMinListLength from 'app/shared/helpers/getMinListLength';
import { MessageTypeEnum } from 'app/shared/enums';
import { defaultFormControls } from 'app/shared/helpers/defaultFormControls';
import { RowMessages } from 'app/components/RowMessages';
import { Alert } from 'app/components/Alert';
import { FormRowValidationMessage } from 'app/components/FormRowValidationMessage';
import { DefaultFormRowContainer } from 'app/components/DefaultFormRowContainer';
import { useFormRow } from './useFormRow';
import { FormRowProps } from './FormRow.interfaces';
import { useStyles } from './FormRow.styles';

export default function FormRow(props: FormRowProps) {
  const {
    formName,
    controlsList = defaultFormControls,
    isLoadingResult,
    className,
    renderValidationMessage: ValidationMessage = FormRowValidationMessage,
    renderFormRowContainer: FormRowContainer = DefaultFormRowContainer,
    withoutDoneAdornment,
    cepikModalControl,
    dataCy,
  } = props;

  const classes = useStyles();

  const {
    isLoading,
    dependsOnFieldValue,
    handleControlValueChange,
    handleControlBlur,
    handleControlValueClick,
    getIsInvalid,
    getInputClasses,
    dictionary,
    isQuestionValid,
    error,
    isQuestionVisible,
    question: {
      type,
      key,
      dependsOn,
      disabled,
      readonly,
      label,
      subLabel,
      helperText,
      answersDictionaryKey,
      tooltip,
      tooltipIcon,
      inlineTooltip,
      minDate,
      maxDate,
      minAge,
      initialFocusedDate,
      datePickerViewType,
      startDate,
      answerGroups,
      required,
      buttonOptions,
      disabledDictionaryEntries,
      width,
      multiline,
      min,
      max,
      openDatePickerOnInputClick,
      placeholder,
      duplicateLabel,
      maxLength,
      noMargin,
      noNegativeMarginTop,
      noPadding,
      customLabelSpacing,
      labelAboveInputField,
      disableCenturyView,
      forceSelect,
      checkedValue,
      uncheckedValue,
    },
    shouldUseParse,
    parseFunction,
  } = useFormRow(props);

  function shouldDisplayRadioGroup() {
    return (
      !(dependsOn && !UtilService.hasValue(dependsOnFieldValue)) &&
      !disabled &&
      !readonly &&
      dictionary.length > 0 &&
      dictionary.length <= getMinListLength()
    );
  }

  const renderControl = () => {
    const component = controlsList[type as keyof typeof controlsList];

    if (!component) {
      return (
        <Alert
          text={`Missing form control ${type}`}
          type={MessageTypeEnum.Danger}
        />
      );
    }

    return (
      <Field
        name={key}
        component={component}
        questionKey={key}
        formName={formName}
        dictionary={dictionary}
        dictionaryKey={answersDictionaryKey}
        answerGroups={answerGroups}
        disabled={disabled}
        label={label}
        subLabel={subLabel}
        helperText={helperText}
        isLoading={isLoading}
        isLoadingResult={isLoadingResult}
        tooltip={tooltip}
        tooltipIcon={tooltipIcon}
        inlineTooltip={inlineTooltip}
        readonly={readonly}
        dependsOn={dependsOn}
        minDate={minDate}
        maxDate={maxDate}
        minAge={minAge}
        initialFocusedDate={initialFocusedDate}
        datePickerViewType={datePickerViewType}
        startDate={startDate}
        isQuestionValid={isQuestionValid}
        dependsOnFieldValue={dependsOnFieldValue}
        onValueChange={handleControlValueChange}
        onControlBlur={handleControlBlur}
        onClick={handleControlValueClick}
        classes={getInputClasses()}
        required={required}
        width={width}
        buttonOptions={buttonOptions}
        disabledDictionaryEntries={disabledDictionaryEntries}
        multiline={multiline}
        min={min}
        max={max}
        openDatePickerOnInputClick={openDatePickerOnInputClick}
        meta={{ valid: !getIsInvalid(), touched: getIsInvalid() }}
        placeholder={placeholder}
        duplicateLabel={duplicateLabel}
        maxLength={maxLength}
        withoutDoneAdornment={withoutDoneAdornment}
        customLabelSpacing={customLabelSpacing}
        labelAboveInputField={labelAboveInputField}
        disableCenturyView={disableCenturyView}
        forceSelect={forceSelect}
        checkedValue={checkedValue}
        uncheckedValue={uncheckedValue}
        {...(shouldUseParse && { parse: parseFunction })}
      />
    );
  };

  if (!isQuestionVisible) return <></>;

  return (
    <FormRowContainer
      className={classNames(
        {
          [classes.additionalMessageSpacing]:
            cepikModalControl && shouldDisplayRadioGroup(),
        },
        className
      )}
      data-field-key={key}
      data-field-error={error?.visible}
      data-field-type="question"
      width={width}
      formName={formName}
      data-cy={dataCy}
      noMargin={noMargin}
      noNegativeMarginTop={noNegativeMarginTop}
      noPadding={noPadding}
    >
      <div className={classes.rowContent}>
        <RowMessages formName={formName} questionKey={key} position={'top'} />
        {renderControl()}
        <ValidationMessage message={error} questionType={type} />
        <RowMessages
          formName={formName}
          questionKey={key}
          position={'bottom'}
        />
      </div>
    </FormRowContainer>
  );
}
